'use client';

import { SvgRight, SvgZoom } from '@/components/icons-eataly';
import { ImageGalleryZoomDialog } from '@/components/image-gallery-custom/image-gallery-zoom-dialog';
import type { GalleryImage } from '@/components/image-gallery-custom/types';
import { cn } from '@/lib/utils';
import { useCallback, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { LABEL_GALLERY_PREVIEW_HEIGHTS, LABEL_GALLERY_PREVIEW_WIDTHS } from '@/lib/images/gallery-sizes';
import DynamicImage from '@/components/dynamic-image';

export interface ProductLabelEtichettaGalleryProps {
    images: GalleryImage[];
    imageAltFallback: string;
}

/**
 * Label tab gallery: large preview, vertical thumbnails, and zoom — matches PDP label etichetta design.
 */
export function ProductLabelGallery({
    images,
    imageAltFallback,
}: ProductLabelEtichettaGalleryProps): ReactElement | null {
    const { t: tProduct } = useTranslation('product');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zoomOpen, setZoomOpen] = useState(false);

    const openZoom = useCallback(() => {
        setZoomOpen(true);
    }, []);

    if (!images.length) {
        return null;
    }

    const safeIndex = selectedIndex < images.length ? selectedIndex : 0;
    const active = images[safeIndex];

    return (
        <>
            <Button
                type="button"
                onClick={openZoom}
                variant="ghost"
                size="icon-large"
                iconAfter={<SvgRight className="size-6" aria-hidden />}
                className="label-l p-0 lg:hidden">
                {tProduct('pimLabelAttributes.openLabelGallery')}
            </Button>
            <div className="flex w-full items-end justify-end gap-4 max-lg:hidden">
                <div className="relative aspect-square min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg">
                    <DynamicImage
                        src={active.src}
                        alt={resolveImageAlt({ alt: active.alt, fallback: imageAltFallback })}
                        className="size-full"
                        loading="lazy"
                        widths={LABEL_GALLERY_PREVIEW_WIDTHS}
                        heights={LABEL_GALLERY_PREVIEW_HEIGHTS}
                        imageProps={{ className: 'size-full object-cover object-center' }}
                    />
                </div>
                <div className="flex shrink-0 flex-col items-center justify-start gap-2.5 self-stretch">
                    {images.length > 1 ? (
                        <div className="flex flex-col items-center gap-2">
                            {images.map((image, index) => (
                                <button
                                    // eslint-disable-next-line react/no-array-index-key -- label shots from catalog; src may repeat
                                    key={`${image.src}-${index}`}
                                    type="button"
                                    onClick={() => setSelectedIndex(index)}
                                    className={cn(
                                        'relative size-[52px] shrink-0 overflow-hidden rounded-lg outline-none',
                                        safeIndex === index
                                            ? 'ring-offset-neutral-white ring-2 ring-[var(--brown-30)] ring-offset-2'
                                            : 'ring-0'
                                    )}
                                    aria-label={`${resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })} (${index + 1} / ${images.length})`}
                                    aria-pressed={safeIndex === index}>
                                    <img
                                        src={image.thumbSrc || image.src}
                                        alt={resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })}
                                        className="size-full object-cover object-center"
                                        loading="lazy"
                                    />
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <button
                        type="button"
                        onClick={openZoom}
                        className="bg-neutral-white flex size-[52px] shrink-0 items-center justify-center rounded-full mt-auto"
                        aria-label={tProduct('zoomIn')}>
                        <SvgZoom className="size-6" aria-hidden />
                    </button>
                </div>
            </div>

            <ImageGalleryZoomDialog
                open={zoomOpen}
                onOpenChange={setZoomOpen}
                images={images}
                imageAltFallback={imageAltFallback}
                initialIndex={safeIndex}
                onSyncSelection={setSelectedIndex}
                isLabelGallery
                title={tProduct('pimLabelAttributes.labelGalleryTitle')}
            />
        </>
    );
}
